import React, { useState, useEffect, useRef } from 'react';
import { ISelectOption } from '@/components/ui/SearchSelectBox';
import SearchSelectBox from '@/components/ui/SearchSelectBox';
import FormHead from '@/components/common/Form/FormHead';
import FieldLayout from '@/components/Layout/FieldLayout';
import PrimaryInput from '@/components/ui/PrimaryInput';
import Label from '@/components/ui/Label';

import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { showToastNotification } from '@/utility/helper';
import {
  IEmployeeOnBoardResponse,
  IEmployeeResponse,
} from '@/interfaces/employee.model';
import { IUser } from '@/interfaces/user.model';

interface OffboardingProfileStepProps {
  setEmployeeData: (data: IUser | null) => void;
  employeeData: IUser | null;
  setUserId: (id: string) => void;
  nextStep: () => void;
  isEditMode?: boolean;
  initialUserId?: string;
  initialEmployeeData?: IUser | null;
}

const OffboardingProfileStep: React.FC<OffboardingProfileStepProps> = ({
  setEmployeeData,
  employeeData,
  setUserId,
  nextStep,
  isEditMode = false,
  initialUserId = '',
  initialEmployeeData = null,
}) => {
  const [employeeOptions, setEmployeeOptions] = useState<ISelectOption[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>(initialUserId);

  const hasFetchedRef = useRef<string>('');

  const splitName = (fullName: string) => {
    if (!fullName) return { firstName: '', lastName: '' };
    const parts = fullName.trim().split(' ');
    if (parts.length === 1) return { firstName: parts[0], lastName: '' };
    const firstName = parts[0];
    const lastName = parts.slice(1).join(' ');
    return { firstName, lastName };
  };

  const { firstName, lastName } = splitName(employeeData?.name || '');

  const getEmployeeList = async () => {
    try {
      setLoadingEmployees(true);
      const response = (await callApi(
        `${API_URL}${API_ENDPOINT.getEmployee}?status=current`,
        {
          method: 'GET',
        },
      )) as IEmployeeOnBoardResponse;
      if (response?.status) {
        const options =
          response?.data?.user?.map((item) => ({
            label: item?.name,
            value: item?.id,
          })) || [];
        setEmployeeOptions(options);
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setLoadingEmployees(false);
    }
  };

  useEffect(() => {
    getEmployeeList();
  }, []);

  // Set initial data in edit mode
  useEffect(() => {
    if (isEditMode && initialEmployeeData) {
      setEmployeeData(initialEmployeeData);
      setUserId(initialUserId);
      hasFetchedRef.current = initialUserId;
    }
  }, [
    isEditMode,
    initialEmployeeData,
    initialUserId,
    setEmployeeData,
    setUserId,
  ]);

  // Fetch employee details – with ref to prevent loop
  useEffect(() => {
    if (isEditMode && employeeData) {
      return;
    }
    if (!selectedUserId) {
      setEmployeeData(null);
      setUserId('');
      hasFetchedRef.current = '';
      return;
    }

    if (hasFetchedRef.current === selectedUserId) {
      return;
    }

    const fetchEmployeeDetail = async () => {
      setLoadingDetail(true);
      try {
        const response: IEmployeeResponse = await callApi(
          API_URL + API_ENDPOINT.getProfile + `?userId=${selectedUserId}`,
          { method: 'GET' },
        );
        if (response?.status) {
          const data = response.data;
          setEmployeeData(data);
          setUserId(selectedUserId);
          hasFetchedRef.current = selectedUserId;
        } else {
          showToastNotification(
            'danger',
            response?.message || 'Failed to fetch employee details',
          );
          setEmployeeData(null);
          setUserId('');
          hasFetchedRef.current = '';
        }
      } catch (error) {
        if (error && typeof error === 'object' && 'message' in error) {
          showToastNotification('danger', (error as IApiErrorResponse).message);
        } else {
          showToastNotification('danger', 'Something went wrong');
        }
        setEmployeeData(null);
        setUserId('');
        hasFetchedRef.current = '';
      } finally {
        setLoadingDetail(false);
      }
    };

    fetchEmployeeDetail();
  }, [selectedUserId, setEmployeeData, setUserId, isEditMode, employeeData]);

  const handleContinue = () => {
    if (!employeeData) {
      showToastNotification('danger', 'Please select an employee');
      return;
    }
    nextStep();
  };

  return (
    <div className="row g-4">
      <div className="col-md-12">
        <label className="form-label">Select Employee *</label>
        <SearchSelectBox
          value={selectedUserId}
          title="Select Employee"
          onChange={(option) => setSelectedUserId(option?.value || '')}
          options={employeeOptions}
          loading={loadingEmployees}
          disable={isEditMode}
          isAsync={false}
          isUsingFilter={false}
        />
      </div>

      {employeeData && (
        <>
          <div className="col-12">
            <FormHead heading="Normal Details" />
            <div className="row">
              <div className="col-12 col-md-6 col-lg-3">
                <div className="mb-3">
                  <Label
                    labelClassname="label-img position-relative"
                    id="profile-image-label"
                  >
                    {employeeData?.profilePhoto ? (
                      <img
                        loading="lazy"
                        src={employeeData.profilePhoto}
                        alt="Profile"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                        }}
                      />
                    ) : (
                      <img src="/employee.svg" alt="default" />
                    )}
                  </Label>
                </div>
              </div>
            </div>
          </div>

          <FieldLayout fieldStyle="col-lg-4">
            <PrimaryInput
              label="Employee Code"
              type="text"
              value={employeeData?.employeeCode?.toString() ?? ''}
              disabled
              readOnly
            />
          </FieldLayout>
          <FieldLayout fieldStyle="col-lg-4">
            <PrimaryInput
              label="First name"
              type="text"
              value={firstName}
              disabled
              readOnly
            />
          </FieldLayout>
          <FieldLayout fieldStyle="col-lg-4">
            <PrimaryInput
              label="Last name"
              type="text"
              value={lastName}
              disabled
              readOnly
            />
          </FieldLayout>
          <FieldLayout fieldStyle="col-lg-4">
            <Label id="gender-label" required>
              Gender
            </Label>
            <select
              className="form-select form-control"
              disabled
              value={
                employeeData?.gender === 0
                  ? 'Male'
                  : employeeData?.gender === 1
                    ? 'Female'
                    : ''
              }
            >
              <option value="">Select gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>
          </FieldLayout>
          <FieldLayout fieldStyle="col-lg-4">
            <PrimaryInput
              label="Email"
              type="email"
              value={employeeData?.email || ''}
              disabled
              readOnly
            />
          </FieldLayout>
          <FieldLayout fieldStyle="col-lg-4">
            <PrimaryInput
              label="Personal Email"
              type="email"
              value={employeeData?.personalEmail || ''}
              disabled
              readOnly
            />
          </FieldLayout>
          <FieldLayout fieldStyle="col-lg-4">
            <PrimaryInput
              label="Mobile No."
              type="text"
              value={employeeData?.phone || ''}
              disabled
              readOnly
            />
          </FieldLayout>
          <FieldLayout fieldStyle="col-lg-4">
            <PrimaryInput
              label="Date of Birth"
              type="date"
              value={
                employeeData?.dob
                  ? new Date(employeeData.dob).toISOString().split('T')[0]
                  : ''
              }
              disabled
              readOnly
            />
          </FieldLayout>
          <FieldLayout fieldStyle="col-lg-4">
            <PrimaryInput
              label="Father name"
              type="text"
              value={employeeData?.userdetail?.fatherName || ''}
              disabled
              readOnly
            />
          </FieldLayout>
          <FieldLayout fieldStyle="col-lg-4">
            <PrimaryInput
              label="Mother name"
              type="text"
              value={employeeData?.userdetail?.motherName || ''}
              disabled
              readOnly
            />
          </FieldLayout>
          <FieldLayout fieldStyle="col-lg-4">
            <PrimaryInput
              label="Emergency Person"
              type="text"
              value={employeeData?.userdetail?.ecpName || ''}
              disabled
              readOnly
            />
          </FieldLayout>
          <FieldLayout fieldStyle="col-lg-4">
            <PrimaryInput
              label="Emergency Contact"
              type="text"
              value={employeeData?.userdetail?.ecpPhone || ''}
              disabled
              readOnly
            />
          </FieldLayout>
          <FieldLayout fieldStyle="col-lg-4">
            <PrimaryInput
              label="Joining Date"
              type="date"
              value={
                employeeData?.joiningDate
                  ? new Date(employeeData.joiningDate)
                      .toISOString()
                      .split('T')[0]
                  : ''
              }
              disabled
              readOnly
            />
          </FieldLayout>
          <FieldLayout fieldStyle="col-lg-4">
            <Label id="blood-group-label" required>
              Blood Group
            </Label>
            <select
              className="form-select form-control"
              disabled
              value={employeeData?.bloodGroup || ''}
            >
              <option value="">Select blood group</option>
              <option value="A+">A+</option>
              <option value="A-">A-</option>
              <option value="B+">B+</option>
              <option value="B-">B-</option>
              <option value="O+">O+</option>
              <option value="O-">O-</option>
              <option value="AB+">AB+</option>
              <option value="AB-">AB-</option>
            </select>
          </FieldLayout>
          <FieldLayout fieldStyle="col-lg-4">
            <Label id="status-label" required>
              Status
            </Label>
            <select
              className="form-select form-control"
              disabled
              value={employeeData?.status || ''}
            >
              <option value="">Select status</option>
              <option value="current">Current</option>
              <option value="exEmployee">Ex-Employee</option>
              <option value="transfer">Transfer</option>
            </select>
          </FieldLayout>
          {employeeData?.status === 'exEmployee' && (
            <FieldLayout fieldStyle="col-lg-4">
              <PrimaryInput
                label="Employee Exit Date"
                type="date"
                value={
                  employeeData?.employeeExitAt
                    ? new Date(employeeData.employeeExitAt)
                        .toISOString()
                        .split('T')[0]
                    : ''
                }
                disabled
                readOnly
              />
            </FieldLayout>
          )}
          <FieldLayout fieldStyle="col-lg-4">
            <Label id="marital-status-label" required>
              Marital Status
            </Label>
            <select
              className="form-select form-control"
              disabled
              value={employeeData?.isMarried ? 'Married' : 'Unmarried'}
            >
              <option value="">Select marital status</option>
              <option value="Married">Married</option>
              <option value="Unmarried">Unmarried</option>
            </select>
          </FieldLayout>
        </>
      )}

      {loadingDetail && (
        <div className="col-12 text-muted small mt-2">
          Loading employee details...
        </div>
      )}

      <div className="col-12 text-end mt-4">
        <button
          type="button"
          className="btn btn-primary px-5"
          onClick={handleContinue}
          disabled={!employeeData}
        >
          Continue to Details
        </button>
      </div>
    </div>
  );
};

export default OffboardingProfileStep;
